/**
 * Maps HTTP status + endpoint kind → human-friendly hint for Apply failures.
 * Keeps error messaging out of the modal component so hints can be tested
 * and iterated on without touching UI code.
 *
 * All hints are ONE sentence. If a status has no specific hint for the
 * endpoint kind, fall back to the generic entry.
 */

const HINTS = {
  // 4xx — client-side / data-drift issues
  400: {
    generic: 'The server rejected the request as invalid — check the row data hasn\'t drifted since preview.',
  },
  403: {
    generic: 'You don\'t have permission for this operation on this row.',
  },
  404: {
    'merge-staging':
      'Source or target no longer in staging — another admin likely already processed this row. Refresh the page.',
    'merge-approved':
      'Source staging row or approved target no longer exists — another admin likely processed it. Refresh the page.',
    skip:
      'Row no longer in staging — already deleted or promoted.',
    keep:
      'Row no longer in staging — already deleted or promoted.',
    generic: 'The referenced row no longer exists — refresh the page.',
  },
  409: {
    'merge-approved':
      'Target approved question is archived or locked — pick a different target or unarchive it first.',
    generic: 'Conflict — the row state has changed since preview. Refresh and try again.',
  },
  422: {
    generic: 'The server rejected the payload as malformed — this is a bug, please report it.',
  },
  429: {
    generic: 'You\'re hitting rate limits — wait 30 seconds and retry.',
  },
  // 5xx — server-side
  500: {
    generic: 'Server error — safe to retry in a few seconds.',
  },
  502: {
    generic: 'Server temporarily unreachable — retry in a moment.',
  },
  503: {
    generic: 'Server unavailable — retry in a moment.',
  },
  504: {
    generic: 'Server took too long to respond — retry with a smaller batch.',
  },
};

// Statuses for which retrying makes sense. 404/403/422 are usually
// permanent for a given payload — retrying just fails the same way.
const RETRYABLE_STATUSES = new Set([0, 429, 500, 502, 503, 504]);

/**
 * @param {number|undefined} status  — HTTP status code (undefined for network errors)
 * @param {string} kind              — 'merge-staging' | 'merge-approved' | 'skip' | 'keep'
 * @returns {{ hint: string, retryable: boolean }}
 */
export function hintForError(status, kind) {
  const s = status || 0;
  const entry = HINTS[s];
  if (!entry) {
    // Network error or unknown status
    if (s === 0) {
      return {
        hint: 'Network error — check your connection and retry.',
        retryable: true,
      };
    }
    return {
      hint: `Unexpected HTTP ${s} — safe to retry, but if it persists please report it.`,
      retryable: RETRYABLE_STATUSES.has(s),
    };
  }
  return {
    hint: entry[kind] || entry.generic,
    retryable: RETRYABLE_STATUSES.has(s),
  };
}

// Exported for tests
export const _RETRYABLE_STATUSES = RETRYABLE_STATUSES;
